/** Feature pillars: copy deck §4. Four pillars ordered by ICP priority:
 * Deliverability → Sending → Leads/Compliance → AI. Each pillar is a 12-col
 * block: brand eyebrow + DM Sans display headline + body on the left, a clean
 * white sub-feature card grid with thin-stroke inline-SVG icons on the right.
 * Pillars separated by hairline rules. */
import type { ReactNode } from 'react';
import { Section, Eyebrow, Rule } from './Section.js';

interface SubFeature {
  icon: ReactNode;
  title: string;
  body: string;
}

interface Pillar {
  id: string;
  eyebrow: string;
  headline: string;
  body: string;
  subFeatures: SubFeature[];
}

/** Minimal geometric icons: 20x20, 1.5px stroke, currentColor. */
const ICON = {
  shield: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M10 2.5 16 5v4.5c0 4-2.6 6.6-6 8-3.4-1.4-6-4-6-8V5l6-2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="m7.5 10 1.8 1.8L13 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  pause: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 7.5v5M12 7.5v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m6.5 10 2.2 2.2L14 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  inbox: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <rect x="2.5" y="4" width="15" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 11.5H7l1.5 2h3l1.5-2h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M10 6v4l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  rotate: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M16 8a6 6 0 1 0 .5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M16 4v4h-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  import: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M10 3v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="m6.5 8 3.5 3 3.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 14v1.5A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  ban: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="m5.2 5.2 9.6 9.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  exit: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M11.5 3.5H5A1.5 1.5 0 0 0 3.5 5v10A1.5 1.5 0 0 0 5 16.5h6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9 10h7.5m0 0L13.5 7m3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  draft: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M4 16h12M5 13l8.5-8.5a1.4 1.4 0 0 1 2 2L7 15l-3 1 1-3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  loop: (
    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5">
      <path d="M10 4a6 6 0 1 1-5.2 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M4 3.5 4.8 7l3.2-.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
} as const;

const PILLARS: Pillar[] = [
  {
    id: 'deliverability',
    eyebrow: 'The core difference',
    headline: 'Guardrails that are on by default, not buried in settings.',
    body: "Deliverability isn't a dashboard you check after the damage is done. It's the engine the whole product runs on. RevBase ramps new mailboxes slowly, caps daily volume per inbox, warms them up with real inbox activity, and pulls the brake automatically the moment bounces or complaints spike. You'd have to work hard to burn a domain on RevBase. That's the point.",
    subFeatures: [
      {
        icon: ICON.inbox,
        title: 'Inbox warmup',
        body: 'Gradual, real inbox activity that builds sender reputation before you send a single cold email.',
      },
      {
        icon: ICON.pause,
        title: 'Auto-pause on spikes',
        body: 'Bounce or complaint rate climbs, RevBase stops the campaign automatically and tells you why, before your domain pays for it.',
      },
      {
        icon: ICON.shield,
        title: 'Auth + spam-score checks',
        body: 'SPF, DKIM, and DMARC verified up front; spam-score testing on your copy before it goes out.',
      },
    ],
  },
  {
    id: 'sending',
    eyebrow: 'Sending',
    headline: 'Multi-step campaigns from your real inbox.',
    body: 'RevBase sends through your own Google Workspace mailbox over OAuth, the same inbox you reply from, which is the single biggest lever on deliverability nobody can fake. Build multi-step drip sequences, schedule by your prospect’s timezone, and let RevBase throttle and rotate across mailboxes so no single inbox gets pushed past its safe limit.',
    subFeatures: [
      {
        icon: ICON.check,
        title: 'Real-inbox sending',
        body: 'Connect your Google Workspace account; send and reply from the inbox prospects already trust. (Outlook support coming.)',
      },
      {
        icon: ICON.clock,
        title: 'Smart scheduling',
        body: "Send windows, per-timezone delivery, and human-paced throttling so your sends don't look like a machine.",
      },
      {
        icon: ICON.rotate,
        title: 'Mailbox rotation + caps',
        body: 'Spread volume across mailboxes with per-inbox daily caps and automatic ramp on new ones.',
      },
    ],
  },
  {
    id: 'leads',
    eyebrow: 'Lists',
    headline: 'Clean lists, compliant by default.',
    body: "Import your contacts, dedupe them, segment them, and suppress the ones you shouldn't touch, without a spreadsheet graveyard. Every send includes a working one-click unsubscribe and honors your suppression list, because CAN-SPAM and GDPR aren't optional and getting them wrong is how complaints (and the spike that auto-pauses you) start.",
    subFeatures: [
      {
        icon: ICON.import,
        title: 'CSV import + dedup',
        body: 'Bring in contacts and companies, merge duplicates, and segment in minutes.',
      },
      {
        icon: ICON.ban,
        title: 'Suppression list',
        body: "Global do-not-contact that's honored across every campaign, automatically.",
      },
      {
        icon: ICON.exit,
        title: 'Compliant unsubscribe',
        body: 'One-click opt-out and required footer handling built into every message, GDPR- and CAN-SPAM-ready.',
      },
    ],
  },
  {
    id: 'ai',
    eyebrow: 'AI · optional',
    headline: "Personalization that doesn't read like a bot.",
    body: "RevBase can draft full sequences and personalize per lead, but it's a tool, not an autopilot. The category's “AI does everything” promise is exactly how inboxes filled up with obviously-generated spam. Use RevBase's AI to get a strong first draft and per-lead context fast, then send something a human would actually reply to. Per-lead AI personalization is a paid add-on (it has real cost behind it), so you only pay for it when it earns its keep.",
    subFeatures: [
      {
        icon: ICON.draft,
        title: 'Sequence drafting',
        body: 'Generate a multi-step campaign skeleton you edit, not a wall of generic AI text you blast.',
      },
      {
        icon: ICON.target,
        title: 'Per-lead personalization',
        body: "Tailor the opener to each contact's context (paid add-on).",
      },
      {
        icon: ICON.loop,
        title: 'You stay in the loop',
        body: 'Every AI draft is yours to approve and edit before a single email sends.',
      },
    ],
  },
];

export function Features() {
  return (
    <Section id="features" className="bg-white">
      <div className="space-y-14 sm:space-y-20">
        {PILLARS.map((pillar, i) => (
          <div key={pillar.id} id={pillar.id} className="scroll-mt-20">
            {i > 0 && <Rule className="mb-14 sm:mb-20" />}
            <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-5">
                <Eyebrow>{pillar.eyebrow}</Eyebrow>
                <h3 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
                  {pillar.headline}
                </h3>
                <p className="mt-5 text-lg leading-relaxed text-slate-600">
                  {pillar.body}
                </p>
              </div>
              <div className="grid gap-4 lg:col-span-7 sm:grid-cols-3 lg:grid-cols-1">
                {pillar.subFeatures.map((sf) => (
                  <div key={sf.title} className="card p-6">
                    <div className="flex items-start gap-4">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                        {sf.icon}
                      </span>
                      <div>
                        <div className="font-display font-semibold text-slate-900">
                          {sf.title}
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                          {sf.body}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
